import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi, listings as listingsApi } from '../services/api';
import { formatPrice } from '../utils/formatters';

export default function PriceAlertButton({ listingId, currentPrice }) {
  const { user } = useAuth();
  const { data, loading, refetch } = useApi(user ? `/listings/${listingId}/price-alert` : null);
  const alert = data?.alert;

  const [open, setOpen] = useState(false);
  const [targetInput, setTargetInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const popoverRef = useRef(null);

  useEffect(() => {
    if (open) setTargetInput((Math.round(currentPrice * 0.8) / 100).toFixed(2));
  }, [open, currentPrice]);

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  async function submit(e) {
    e.preventDefault();
    const dollars = parseFloat(targetInput);
    if (!dollars || dollars <= 0) {
      setError('Enter a valid target price.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await listingsApi.setPriceAlert(listingId, Math.round(dollars * 100));
      setOpen(false);
      refetch();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not set price alert.');
    } finally {
      setSaving(false);
    }
  }

  async function removeAlert() {
    setSaving(true);
    try {
      await listingsApi.removePriceAlert(listingId);
      refetch();
    } finally {
      setSaving(false);
    }
  }

  if (!user) {
    return (
      <Link to="/login" className="btn btn-outline btn-full">
        Log in to set a price alert
      </Link>
    );
  }

  if (loading) return null;

  if (alert) {
    return (
      <button type="button" className="btn btn-outline btn-full" disabled={saving} onClick={removeAlert}>
        {saving ? 'Removing…' : `Alert set for ${formatPrice(alert.target_price)} — Remove alert`}
      </button>
    );
  }

  return (
    <div style={{ position: 'relative' }} ref={popoverRef}>
      <button type="button" className="btn btn-outline btn-full" onClick={() => setOpen(v => !v)}>
        Set Price Alert
      </button>

      {open && (
        <div className="price-alert-popover">
          <p className="price-alert-popover-text">
            We'll email you as soon as this stay's price drops to or below your target.
          </p>
          <form onSubmit={submit}>
            <div className="form-group" style={{ marginBottom: '0.6rem' }}>
              <label htmlFor="price-alert-target">Target price ($)</label>
              <input
                id="price-alert-target"
                type="number"
                min="1"
                step="0.01"
                required
                value={targetInput}
                onChange={e => setTargetInput(e.target.value)}
              />
            </div>
            {error && <div className="alert alert-error" style={{ padding: '0.5rem 0.75rem', fontSize: 'var(--text-sm)' }}>{error}</div>}
            <button type="submit" className="btn btn-primary btn-full btn-sm" disabled={saving}>
              {saving ? 'Setting…' : 'Set Alert'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
